import type { ProjectedRecord } from "@storyboard/driver-query/predicate";
import type { DriverRow } from "@/types/driver";
import type { DispatchItem } from "./dispatchItems";

/**
 * One Dispatch table row at either grain: a driver, or one message on a driver's channel. Both carry the driver and a
 * flattened record, projected once so filters, sort, and cells agree.
 *
 * `id` is what the table, the highlight, and the expanded row key on: the membership id or the item id, except when
 * grouping lands one row under several groups and each copy needs its own. A driver row carries `unacknowledgedCount`,
 * how many of the driver's messages wait on a dispatcher (zero when the channel is handled), beside the driver's read
 * state; a message row's item carries both for the message.
 * The store calls a message an `Item`, so the message row holds a `DispatchItem`.
 */
export type DispatchTableRow =
  | { kind: "driver"; id: string; driver: DriverRow; unacknowledgedCount: number; record: ProjectedRecord }
  | { kind: "message"; id: string; item: DispatchItem; driver: DriverRow; record: ProjectedRecord };

/** Whether the row's Message describes the driver's photo rather than quoting the driver: the message, or the latest. */
export function rowMessageIsPhoto(row: DispatchTableRow): boolean {
  return row.kind === "message" ? row.item.photo : row.driver.recentSource === "photo";
}

/** Whether anyone has read what the row is about: the message, or the driver's channel. Painted as the row tint. */
export function rowUnread(row: DispatchTableRow): boolean {
  return row.kind === "message" ? row.item.unread : row.driver.unread === true;
}

/** Whether the row still waits on a dispatcher: the message, or any message on the channel. Painted as the red dot. */
export function rowUnacknowledged(row: DispatchTableRow): boolean {
  return row.kind === "message" ? !row.item.acknowledged : row.unacknowledgedCount > 0;
}

/**
 * The rows showing the red dot, one id per row: a driver row counts once however many of its messages wait.
 */
export function unacknowledgedRowIds(rows: readonly DispatchTableRow[]): string[] {
  return rows.filter(rowUnacknowledged).map((row) => row.id);
}

/** The feed a reply to the row goes to and its History opens: the message's feed, or the driver's alias feed. */
export function rowFeedId(row: DispatchTableRow): string | null {
  return row.kind === "message" ? row.item.feedId : row.driver.feedId;
}
